/**
 * @fileoverview Schema Manifest Editor — admin tool for shaping the legacy SQL table catalog.
 * @module system-web/components/migration/SchemaManifestEditor
 *
 * Responsibilities:
 * - Display all tables from the persisted legacy SQL schema manifest in the shared grid.
 * - Let admins disable tables that are irrelevant to any migration.
 * - Let admins review SQL-declared FKs and declare missing relationships where Streamline has gaps.
 * - Show column details from the persisted manifest without runtime API fetches.
 * - Auto-populate the manifest on first open; show a Refresh button afterward.
 * - Provide Save / Discard controls for pending changes.
 *
 * Dependencies:
 * - useLegacySchemaManifest hook
 * - TableDetailSlideIn / TableDetailSlideInHeader components
 * - lucide-react icons
 *
 * Architecture:
 * - Local-first: manifest state lives in Dexie, subscribed via useLegacySchemaManifest liveQuery.
 * - Pending edits (disable toggles, FK additions) are held in local hook state until the admin
 *   clicks Save. Save POSTs to server; Dexie updates via system sync pull.
 * - Column details are read from the Dexie-synced manifest; no lazy API column fetches.
 * - The Add FK modal is an inline state machine — no separate file.
 *
 * Notes:
 * - On first open with no saved manifest: "Building manifest from legacy SQL…" progress state.
 * - After manifest exists: full table grid + Refresh button in toolbar.
 * - Enabled and disabled tables stay in one standard grid, with Status filtering via dropdown.
 * - Table details render in the shared GridPageShell slideout with four tabs.
 *
 * @reusable
 * Usage: Rendered as the top-level "Schema Manifest" tab in LegacyMigrationPage.
 * Rules: stateful (hook state); receives legacyDb from parent.
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  Loader2, RefreshCw, Save, Undo2, AlertTriangle, Table2,
  Link, Plus, CheckCircle2, Database, ChevronDown,
} from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter, DataGridRowAction } from '@ui-admin/components/DataGrid';
import { useLegacySchemaManifest } from '../../hooks/useLegacySchemaManifest';
import type { SchemaManifestFk, SchemaManifestTableEntry } from '../../services/legacySchemaManifestService';
import { TableDetailSlideIn, TableDetailSlideInHeader } from './TableDetailSlideIn';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract SchemaManifestEditorProps
 *
 * Purpose:
 * - Props for the SchemaManifestEditor component.
 *
 * Fields:
 * - legacyDb: The legacy database whose schema manifest to display and edit.
 *             null when no tenant is selected (editor renders a placeholder).
 *
 * Rules:
 * - legacyDb must be 'SLInspectionsDB001' or 'SLInspectionsDB002' when provided.
 *
 * Sync:
 * - Manifest state from Dexie via useLegacySchemaManifest.
 */
export interface SchemaManifestEditorProps {
  legacyDb: string | null;
}

// ---------------------------------------------------------------------------
// Filter view options
// ---------------------------------------------------------------------------

type TableFilterView = 'all' | 'enabled' | 'disabled';

// ---------------------------------------------------------------------------
// Add FK Modal
// ---------------------------------------------------------------------------

interface AddFkModalProps {
  tables: SchemaManifestTableEntry[];
  onConfirm: (fk: SchemaManifestFk) => void;
  onCancel: () => void;
}

function AddFkModal({ tables, onConfirm, onCancel }: AddFkModalProps) {
  const activeTables = tables.filter((t) => !t.disabled);

  const [parentTable, setParentTable] = useState('');
  const [parentColumn, setParentColumn] = useState('');
  const [childTable, setChildTable] = useState('');
  const [childColumn, setChildColumn] = useState('');
  const [label, setLabel] = useState('');
  const parentCols = activeTables.find((t) => t.table === parentTable)?.columns ?? [];
  const childCols = activeTables.find((t) => t.table === childTable)?.columns ?? [];

  const handleParentTableChange = useCallback((table: string) => {
    setParentTable(table);
    setParentColumn('');
  }, []);

  const handleChildTableChange = useCallback((table: string) => {
    setChildTable(table);
    setChildColumn('');
  }, []);

  const canConfirm = parentTable && parentColumn && childTable && childColumn;

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;
    const parent = activeTables.find((t) => t.table === parentTable);
    const child = activeTables.find((t) => t.table === childTable);
    onConfirm({
      parentSchema: parent?.schema,
      parentTable,
      parentColumn,
      childSchema: child?.schema,
      childTable,
      childColumn,
      label: label.trim() || undefined,
    });
  }, [activeTables, canConfirm, parentTable, parentColumn, childTable, childColumn, label, onConfirm]);

  const selectCls = 'w-full text-sm border rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500';
  const labelCls = 'text-xs font-medium text-gray-600 mb-1 block';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <Link className="h-4 w-4 text-blue-600" />
          Declare Foreign Key Relationship
        </h2>

        <div className="space-y-3">
          {/* Parent (referenced) side */}
          <div className="p-3 rounded-lg border" style={{ borderColor: 'rgba(37,99,235,0.2)', background: 'rgba(37,99,235,0.03)' }}>
            <p className="text-xs font-semibold text-blue-700 mb-2">Parent Table (referenced)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Table</label>
                <select className={selectCls} value={parentTable} onChange={(e) => handleParentTableChange(e.target.value)}>
                  <option value="">Select table…</option>
                  {activeTables.map((t) => <option key={t.table} value={t.table}>{t.table}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Column</label>
                <select className={selectCls} value={parentColumn} onChange={(e) => setParentColumn(e.target.value)} disabled={!parentTable}>
                  <option value="">Select column…</option>
                  {parentCols.map((c) => <option key={c.column} value={c.column}>{c.column}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center text-xs text-gray-400 gap-2">
            <div className="h-px flex-1 bg-gray-200" />
            <span>referenced by ↓</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          {/* Child (referencing) side */}
          <div className="p-3 rounded-lg border" style={{ borderColor: 'rgba(22,163,74,0.2)', background: 'rgba(22,163,74,0.03)' }}>
            <p className="text-xs font-semibold text-green-700 mb-2">Child Table (referencing)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Table</label>
                <select className={selectCls} value={childTable} onChange={(e) => handleChildTableChange(e.target.value)}>
                  <option value="">Select table…</option>
                  {activeTables.map((t) => <option key={t.table} value={t.table}>{t.table}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Column</label>
                <select className={selectCls} value={childColumn} onChange={(e) => setChildColumn(e.target.value)} disabled={!childTable}>
                  <option value="">Select column…</option>
                  {childCols.map((c) => <option key={c.column} value={c.column}>{c.column}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Label */}
          <div>
            <label className={labelCls}>Label (optional)</label>
            <input
              type="text"
              className="w-full text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. codeset → chapters (one-to-many)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onCancel} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-40"
            style={{ background: '#2563eb' }}
          >
            Add Relationship
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component SchemaManifestEditor
 *
 * Purpose:
 * - Admin tool for viewing and shaping the full legacy SQL table catalog before
 *   building migration datasets.
 *
 * Props:
 * - See SchemaManifestEditorProps.
 *
 * Behavior:
 * - On first open (no saved manifest): auto-populates from INFORMATION_SCHEMA with
 *   a "Building manifest…" progress state.
 * - After manifest loaded: shows all tables in the shared grid.
 * - Row actions disable/enable tables without removing them from the listing.
 * - Row click opens the standard GridPageShell slideout with four tabs:
 *   General, Columns, Foreign Keys, Relationships.
 * - Slide-in header shows the table name, an Enable/Disable toggle button, and close.
 * - Toolbar: filter dropdown (All / Enabled / Disabled) | Add Relationship | Refresh | Save | Discard.
 * - Grid columns per §3.9: Table, Status, Columns, Foreign Keys, Relationships.
 *
 * Data Source:
 * - local (Dexie via useLegacySchemaManifest liveQuery)
 *
 * Constraints:
 * - No direct apiFetch calls from this component.
 * - All mutations go through useLegacySchemaManifest.
 *
 * Performance:
 * - Column details are pre-populated server-side and read from Dexie; no per-row fetches.
 *
 * @reusable
 * Usage: Rendered as the "Schema Manifest" tab in LegacyMigrationPage.
 * Rules: stateful (pending edits, modal open state, filter view, selected row).
 */
export function SchemaManifestEditor({ legacyDb }: SchemaManifestEditorProps) {
  const {
    manifest,
    pendingTables,
    pendingFks,
    isDirty,
    isLoading,
    isPopulating,
    isSaving,
    error,
    toggleTableDisabled,
    toggleColumnDisabled,
    addFk,
    removeFk,
    save,
    discard,
    triggerPopulate,
  } = useLegacySchemaManifest(legacyDb);

  const [showAddFkModal, setShowAddFkModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [filterView, setFilterView] = useState<TableFilterView>('enabled');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const handleAddFkConfirm = useCallback((fk: SchemaManifestFk) => {
    addFk(fk);
    setShowAddFkModal(false);
  }, [addFk]);

  // ---------------------------------------------------------------------------
  // Derived counts
  // ---------------------------------------------------------------------------
  const { enabledCount, disabledCount } = useMemo(
    () => pendingTables.reduce(
      (counts, table) => {
        if (table.disabled) counts.disabledCount += 1;
        else counts.enabledCount += 1;
        return counts;
      },
      { enabledCount: 0, disabledCount: 0 },
    ),
    [pendingTables],
  );

  // ---------------------------------------------------------------------------
  // Filtered and sorted rows
  // ---------------------------------------------------------------------------
  const gridRows = useMemo(() => {
    const filtered = pendingTables.filter((t) => {
      if (filterView === 'enabled') return !t.disabled;
      if (filterView === 'disabled') return t.disabled;
      return true;
    });
    return [...filtered].sort((a, b) => `${a.schema}.${a.table}`.localeCompare(`${b.schema}.${b.table}`));
  }, [pendingTables, filterView]);

  const canAddRelationship = enabledCount >= 2;

  const selectedEntry = useMemo(
    () => pendingTables.find((table) => table.table === selectedTable) ?? null,
    [pendingTables, selectedTable],
  );

  // ---------------------------------------------------------------------------
  // Row actions
  // ---------------------------------------------------------------------------
  const rowActions = useMemo<DataGridRowAction<SchemaManifestTableEntry>[]>(() => [
    {
      id: 'disable',
      label: 'Disable Table',
      icon: ChevronDown,
      variant: 'default' as const,
      onClick: (row) => toggleTableDisabled(row.table),
      hidden: (row) => row.disabled,
    },
    {
      id: 'enable',
      label: 'Enable Table',
      icon: ChevronDown,
      variant: 'default' as const,
      onClick: (row) => toggleTableDisabled(row.table),
      hidden: (row) => !row.disabled,
    },
  ], [toggleTableDisabled]);

  // ---------------------------------------------------------------------------
  // DataGrid filters (Status column filter — passed to DataGrid machinery)
  // ---------------------------------------------------------------------------
  const tableFilters = useMemo<DataGridFilter[]>(() => [
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Enabled', label: 'Enabled' },
        { value: 'Disabled', label: 'Disabled' },
      ],
    },
  ], []);

  // ---------------------------------------------------------------------------
  // Grid columns — §3.9 spec: Table | Status | Columns | Foreign Keys | Relationships
  // ---------------------------------------------------------------------------
  const tableColumns = useMemo<DataGridColumn<SchemaManifestTableEntry>[]>(() => [
    {
      id: 'table',
      header: 'Table',
      accessorKey: 'table',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs">{row.original.schema}.{row.original.table}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => (row.disabled ? 'Disabled' : 'Enabled'),
      sortable: true,
      cell: ({ row }: any) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          row.original.disabled
            ? 'bg-gray-100 text-gray-600 border border-gray-200'
            : 'bg-green-100 text-green-700 border border-green-200'
        }`}>
          {row.original.disabled ? 'Disabled' : 'Enabled'}
        </span>
      ),
    },
    {
      id: 'columns',
      header: 'Columns',
      accessorFn: (row) => row.columns?.length ?? 0,
      sortable: true,
    },
    {
      id: 'foreignKeys',
      header: 'Foreign Keys',
      accessorFn: (row) =>
        pendingFks.filter((fk) => fk.parentTable === row.table || fk.childTable === row.table).length,
      sortable: true,
    },
    {
      id: 'relationships',
      header: 'Relationships',
      accessorFn: (row) =>
        pendingFks.filter((fk) => fk.parentTable === row.table || fk.childTable === row.table).length,
      sortable: true,
    },
  ], [pendingFks]);

  // ---------------------------------------------------------------------------
  // Filter dropdown label helpers
  // ---------------------------------------------------------------------------
  const filterLabels: Record<TableFilterView, string> = {
    all: 'All Tables',
    enabled: 'Enabled Tables',
    disabled: 'Disabled Tables',
  };

  // ---------------------------------------------------------------------------
  // No tenant selected
  // ---------------------------------------------------------------------------
  if (!legacyDb) {
    return (
      <div className="flex flex-col items-center justify-center py-16 opacity-40 gap-3">
        <Database className="h-10 w-10" />
        <p className="text-sm">Select a department to view its schema manifest.</p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Loading / populating
  // ---------------------------------------------------------------------------
  if (isLoading || (isPopulating && !manifest)) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <div className="text-center">
          <p className="text-sm font-medium">
            {isPopulating ? 'Building manifest from legacy SQL…' : 'Loading manifest…'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {isPopulating
              ? `Fetching all tables from ${legacyDb} INFORMATION_SCHEMA. This may take a moment.`
              : 'Reading saved manifest from local database.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col">
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 shrink-0">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <GridPageShell<SchemaManifestTableEntry>
        gridId={`legacy-schema-manifest-${legacyDb}`}
        gridType="legacy-schema-manifest"
        title="Schema Manifest"
        subtitle={`${enabledCount} active · ${disabledCount} disabled · ${pendingFks.length} FK${pendingFks.length !== 1 ? 's' : ''}${isDirty ? ' · Unsaved changes' : ''}`}
        columns={tableColumns}
        data={gridRows}
        getRowId={(row) => `${row.schema}.${row.table}`}
        filters={tableFilters}
        rowActions={rowActions}
        onRowClick={(row) => setSelectedTable(row.table)}
        activeRowId={selectedEntry ? `${selectedEntry.schema}.${selectedEntry.table}` : null}
        searchPlaceholder="Search legacy SQL tables…"
        defaultPageSize={50}
        toolbarLeftExtra={
          <div className="flex items-center gap-2">
            {manifest && (
              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="h-3 w-3" />
                Manifest loaded
              </span>
            )}
            {/* Filter dropdown: All Tables / Enabled Tables (default) / Disabled Tables */}
            <div className="relative">
              <button
                onClick={() => setFilterDropdownOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {filterLabels[filterView]}
                <ChevronDown className="h-3 w-3" />
              </button>
              {filterDropdownOpen && (
                <div
                  className="absolute left-0 top-full mt-1 w-40 rounded-lg border border-gray-200 bg-white shadow-lg z-20"
                  onBlur={() => setFilterDropdownOpen(false)}
                >
                  {(['enabled', 'all', 'disabled'] as TableFilterView[]).map((view) => (
                    <button
                      key={view}
                      onClick={() => { setFilterView(view); setFilterDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                        filterView === view ? 'text-orange-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      {filterLabels[view]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        }
        toolbarActions={
          <div className="flex items-center gap-2">
            {isPopulating && (
              <span className="text-xs text-blue-600 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Refreshing from SQL…
              </span>
            )}
            <button
              onClick={() => setShowAddFkModal(true)}
              disabled={!canAddRelationship}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
              title="Declare a FK relationship between two tables"
            >
              <Plus className="h-4 w-4" />
              Add Relationship
            </button>
            <button
              onClick={() => void triggerPopulate()}
              disabled={isPopulating || isSaving}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Refresh table list from INFORMATION_SCHEMA (preserves your disable flags and FKs)"
            >
              <RefreshCw className={`h-4 w-4 ${isPopulating ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {isDirty && (
              <>
                <button
                  onClick={discard}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <Undo2 className="h-4 w-4" />
                  Discard
                </button>
                <button
                  onClick={() => void save()}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </button>
              </>
            )}
          </div>
        }
        emptyIcon={Table2}
        emptyTitle="No schema tables"
        emptySubtitle="Refresh from SQL to populate the schema manifest."
        panelHeader={selectedEntry ? (
          <TableDetailSlideInHeader
            table={selectedEntry}
            onToggleDisabled={toggleTableDisabled}
            onClose={() => setSelectedTable(null)}
          />
        ) : undefined}
        onPanelClose={() => setSelectedTable(null)}
        panelContent={selectedEntry ? (
          <TableDetailSlideIn
            table={selectedEntry}
            fks={pendingFks}
            onRemoveFk={removeFk}
            onToggleColumnDisabled={toggleColumnDisabled}
          />
        ) : null}
        onRefresh={() => void triggerPopulate()}
        exportable
        selectable
      />

      {/* Add FK modal */}
      {showAddFkModal && (
        <AddFkModal
          tables={pendingTables}
          onConfirm={handleAddFkConfirm}
          onCancel={() => setShowAddFkModal(false)}
        />
      )}
    </div>
  );
}
